import { EntityKind } from "../../utils/entityPaths";

type MergerAnswerKey =
  | "kycCheckOnMft"
  | "kycCheckOnCounterparty"
  | "kycCheckOnBroker"
  | "agreementType"
  | "agreementReview"
  | "ndaReview"
  | "mandate"
  | "collateral"
  | "tradeCapture"
  | "tradeConfirmation";

/**
 * The Market Access answers a merger fills in on its create form instead of in a lifecycle.
 * Stored on marketAccessLifecycle all the same, where the other dialogs already read them.
 */
export type MergerAnswers = Partial<Record<MergerAnswerKey, string>>;

export type MergerAnswerField = {
  key: keyof MergerAnswers;
  label: string;
  /** "needed" is a needed / not needed select; "text" is free text. */
  input: "needed" | "text";
  /** Required where the Market Access dialog's Complete requires it. */
  required: boolean;
};

const kycOnMft: MergerAnswerField = {
  key: "kycCheckOnMft",
  label: "KYC Check (on MFT)",
  input: "needed",
  required: true
};
const agreementType: MergerAnswerField = {
  key: "agreementType",
  label: "Agreement Type",
  input: "text",
  required: true
};
const agreementReview: MergerAnswerField = {
  key: "agreementReview",
  label: "Agreement Review",
  input: "needed",
  required: true
};
const ndaReview: MergerAnswerField = {
  key: "ndaReview",
  label: "NDA Review",
  input: "needed",
  required: true
};
const tradeConfirmation: MergerAnswerField = {
  key: "tradeConfirmation",
  label: "Trade Confirmation",
  input: "needed",
  required: false
};

/**
 * Same fields, labels and required set as each book's Market Access dialog, which a merger skips.
 * Brokers have no collateral, trade capture, treasury or tech; their legacy reportingRequirements
 * is left out (superseded by Compliance's remitReporting, ADHO-453).
 */
export const MERGER_ANSWER_FIELDS: Record<EntityKind, MergerAnswerField[]> = {
  counterparty: [
    kycOnMft,
    {
      key: "kycCheckOnCounterparty",
      label: "KYC Check/sanction screening (on Counterparty)",
      input: "needed",
      required: true
    },
    agreementType,
    agreementReview,
    ndaReview,
    { key: "mandate", label: "Mandate", input: "needed", required: true },
    { key: "collateral", label: "Collateral", input: "needed", required: true },
    {
      key: "tradeCapture",
      label: "Trade Capture",
      input: "needed",
      required: true
    },
    tradeConfirmation
  ],
  broker: [
    kycOnMft,
    {
      key: "kycCheckOnBroker",
      label: "KYC Check/sanction screening (on Broker)",
      input: "needed",
      required: true
    },
    agreementType,
    agreementReview,
    ndaReview,
    {
      key: "mandate",
      label: "Mandate review",
      input: "needed",
      required: true
    },
    tradeConfirmation
  ]
};

export const NEEDED_LABELS: Record<string, string> = {
  needed: "Needed",
  not_needed: "Not needed"
};

/** Only the answers that exist for this kind, so switching type cannot carry e.g. collateral over. */
export const pickAnswers = (
  kind: EntityKind,
  source: MergerAnswers | null | undefined
): MergerAnswers =>
  Object.fromEntries(
    MERGER_ANSWER_FIELDS[kind].map((field) => [
      field.key,
      source?.[field.key] ?? ""
    ])
  );

export const answersComplete = (kind: EntityKind, answers: MergerAnswers) =>
  MERGER_ANSWER_FIELDS[kind].every(
    (field) => !field.required || Boolean(answers[field.key]?.trim())
  );

/** Empty strings back to undefined, so an unanswered optional field is not stored as "". */
export const toLifecycleAnswers = (
  kind: EntityKind,
  answers: MergerAnswers
): MergerAnswers =>
  Object.fromEntries(
    MERGER_ANSWER_FIELDS[kind].map((field) => [
      field.key,
      answers[field.key]?.trim() || undefined
    ])
  );
